/**
 * Attach Firebase ID tokens to Flask API calls (G1).
 *
 * Skips `/admin/*` so the ops HMAC Bearer is not overwritten.
 * Must be installed after `initializeApp(firebaseConfig)`.
 *
 * Waits for `authStateReady()` before reading `currentUser` — otherwise
 * dashboard bootstrap POSTs race ahead of Firebase restoring the session
 * and go out with no Authorization header → 401 auth_required.
 *
 * Project pairing: FE `VUE_APP_FIREBASE_PROJECT_ID` must match backend
 * `COINPROOF_FIREBASE_PROJECT_ID` + Admin SDK JSON (local: coinproof-dev,
 * prod: coinproof-c66d5). Mismatch → 401 invalid_token.
 */

import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { apiBaseUrl } from '@/config';
import { isNetworkError, markApiUnreachable } from '@/utils/connectivity';

function isFlaskApiUrl(url) {
  if (!url || typeof url !== 'string') return false;
  // Absolute URL to our API, or relative path used in tests.
  if (url.startsWith(apiBaseUrl)) return true;
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  return false;
}

function isAdminUrl(url) {
  return typeof url === 'string' && url.includes('/admin/');
}

function isAnonymousFeedbackUrl(url) {
  // Public survey. No Firebase session, and no account token on the request.
  return typeof url === 'string'
    && /\/feedback(?:$|\?)/.test(url)
    && !url.includes('/admin/');
}

let _authReadyPromise = null;

function waitForAuthReady() {
  if (!_authReadyPromise) {
    const auth = getAuth();
    _authReadyPromise = typeof auth.authStateReady === 'function'
      ? auth.authStateReady()
      : Promise.resolve();
  }
  return _authReadyPromise;
}

export function installApiAuth() {
  axios.interceptors.request.use(async (config) => {
    const url = config.url || '';
    if (!isFlaskApiUrl(url) || isAdminUrl(url) || isAnonymousFeedbackUrl(url)) {
      return config;
    }
    try {
      await waitForAuthReady();
    } catch (err) {
      console.warn('[coinproof] authStateReady failed', err);
    }
    const user = getAuth().currentUser;
    if (!user) {
      const method = String(config.method || 'get').toLowerCase();
      // Signed-in calls with no user become a wall of 401s. Drop them
      // instead of hitting Flask. Public GETs (board data) may still run.
      if (method !== 'get') {
        const err = new Error('Not signed in');
        err.code = 'auth/no-user';
        return Promise.reject(err);
      }
      return config;
    }
    try {
      const token = await user.getIdToken();
      config.headers = config.headers || {};
      if (typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      // Token refresh needs the network. Sending the call with no token
      // makes Flask answer 401 and looks like a logout.
      console.warn('[coinproof] failed to attach Firebase ID token', err);
      return Promise.reject(err);
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        markApiUnreachable(false);
      }
      return response;
    },
    async (error) => {
      const status = error?.response?.status;
      const config = error?.config;
      const url = config?.url || '';
      // One forced refresh. A cached ID token can be rejected right after
      // a wake; retrying forever is what fills the console with 401s.
      if (
        status === 401
        && config
        && !config._authRetried
        && isFlaskApiUrl(url)
        && !isAdminUrl(url)
        && !isAnonymousFeedbackUrl(url)
      ) {
        const user = getAuth().currentUser;
        if (user) {
          config._authRetried = true;
          try {
            const token = await user.getIdToken(true);
            config.headers = config.headers || {};
            if (typeof config.headers.set === 'function') {
              config.headers.set('Authorization', `Bearer ${token}`);
            } else {
              config.headers.Authorization = `Bearer ${token}`;
            }
            return axios.request(config);
          } catch (refreshErr) {
            console.warn('[coinproof] token refresh after 401 failed', refreshErr);
          }
        }
      }
      if (isNetworkError(error)) {
        markApiUnreachable(true);
      }
      return Promise.reject(error);
    }
  );
}
